import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Clock, RefreshCw, Brain, CheckCircle, XCircle } from 'lucide-react';
import api from '../../../services/api';

interface AdvisorTabProps {
  dotNumber: string;
}

interface Assessment {
  assessment: string;
  carrierName: string;
  dotNumber: string;
  model: string;
  mode: string;
  generatedAt: string;
  cached?: boolean;
}

export const AdvisorTab: React.FC<AdvisorTabProps> = ({ dotNumber }) => {
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'consultant' | 'vetting'>('consultant');
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (loading) {
      const start = Date.now();
      interval = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const generateAssessment = async () => {
    setLoading(true);
    setError(null);
    setElapsed(0);
    try {
      const response = await api.post('/advisor/assessment', { dot: dotNumber, mode }, { timeout: 600000 });
      setAssessment(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to generate assessment');
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (text: string) => {
    const upper = text.toUpperCase();
    if (upper.includes('CRITICAL')) return '#b3412c';
    if (upper.includes('HIGH')) return '#963524';
    if (upper.includes('MODERATE')) return '#7d561c';
    if (upper.includes('LOW')) return '#4b5c3a';
    return '#857f73';
  };

  // Model output is untrusted: escape it before turning markdown into HTML.
  const escapeHtml = (text: string) =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  const formatMarkdown = (text: string) => {
    return escapeHtml(text)
      .replace(/### (.*)/g, '<h3 style="font-size:1.1rem;font-weight:700;margin:1.2rem 0 0.5rem;color:#23211d">$1</h3>')
      .replace(/## (.*)/g, '<h2 style="font-size:1.25rem;font-weight:700;margin:1.5rem 0 0.5rem;color:#23211d">$1</h2>')
      .replace(/\*\*(.*?)\*\*/g, '<strong style="color:#23211d">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\| (.*?) \|/g, (match) => {
        const cells = match.split('|').filter(c => c.trim());
        return '<div style="display:grid;grid-template-columns:repeat(' + cells.length + ',1fr);gap:0.5rem;padding:0.25rem 0;border-bottom:1px solid #e7e2d6">' + 
          cells.map(c => '<span style="font-size:0.85rem">' + c.trim() + '</span>').join('') + '</div>';
      })
      .replace(/\n/g, '<br/>');
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Brain size={24} color="#6d685e" />
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#23211d', margin: 0 }}>
            AI Safety Advisor
          </h2>
          <span style={{ fontSize: '0.75rem', background: '#4a463f', color: 'white', padding: '0.15rem 0.5rem', borderRadius: '9999px' }}>
            {assessment?.model ?? 'AI model'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setMode('consultant')}
            style={{
              padding: '0.4rem 0.8rem', borderRadius: '0.375rem', fontSize: '0.8rem', cursor: 'pointer',
              background: mode === 'consultant' ? '#4a463f' : 'transparent',
              color: mode === 'consultant' ? 'white' : '#4a463f',
              border: `1px solid ${mode === 'consultant' ? '#4a463f' : '#4a463f'}`
            }}
          >
            🏥 Consultant
          </button>
          <button
            onClick={() => setMode('vetting')}
            style={{
              padding: '0.4rem 0.8rem', borderRadius: '0.375rem', fontSize: '0.8rem', cursor: 'pointer',
              background: mode === 'vetting' ? '#34312c' : 'transparent',
              color: mode === 'vetting' ? 'white' : '#4a463f',
              border: `1px solid ${mode === 'vetting' ? '#34312c' : '#4a463f'}`
            }}
          >
            🔍 Vetting
          </button>
        </div>
      </div>

      {/* Description */}
      <div style={{ background: '#f4f1ea', border: '1px solid #e7e2d6', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem' }}>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#4a463f' }}>
          {mode === 'consultant' 
            ? '🏥 Consultant mode analyzes the carrier\'s full safety profile and provides actionable remediation steps — like having an expert safety consultant review the operation.'
            : '🔍 Vetting mode gives a quick GO / CAUTION / NO-GO recommendation for brokers deciding whether to use this carrier.'}
        </p>
      </div>

      {/* Generate Button */}
      {!loading && (
        <button
          onClick={generateAssessment}
          style={{
            width: '100%', padding: '0.75rem', borderRadius: '0.5rem', fontSize: '1rem', fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            background: 'var(--color-primary)', color: 'white', border: 'none'
          }}
        >
          {assessment ? <RefreshCw size={18} /> : <Brain size={18} />}
          {assessment ? 'Regenerate Assessment' : 'Generate AI Assessment'}
        </button>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', background: '#f4f1ea', borderRadius: '0.5rem', border: '1px solid #e7e2d6' }}>
          <div style={{ animation: 'spin 2s linear infinite', display: 'inline-block', marginBottom: '1rem' }}>
            <Brain size={40} color="#4a463f" />
          </div>
          <p style={{ color: '#4a463f', fontSize: '0.95rem', margin: '0.5rem 0' }}>
            Analyzing carrier safety data with AI...
          </p>
          <p style={{ color: '#4a463f', fontSize: '1.5rem', fontWeight: 700, margin: '0.5rem 0' }}>
            {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
          </p>
          <p style={{ color: '#6d685e', fontSize: '0.8rem' }}>
            Typically takes 2-3 minutes for a full analysis
          </p>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '0.5rem', padding: '1rem', marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <XCircle size={18} color="#b3412c" />
          <span style={{ color: '#963524', fontSize: '0.9rem' }}>{error}</span>
        </div>
      )}

      {/* Assessment Result */}
      {assessment && !loading && (
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CheckCircle size={16} color="#5d7148" />
              <span style={{ color: '#6d685e', fontSize: '0.8rem' }}>
                Generated {new Date(assessment.generatedAt).toLocaleString()} • {assessment.model}
              </span>
            </div>
          </div>
          <div 
            style={{
              background: '#ffffff', border: '1px solid #e7e2d6', borderRadius: '0.5rem',
              padding: '1.5rem', color: '#34312c', fontSize: '0.9rem', lineHeight: 1.7,
              maxHeight: '70vh', overflowY: 'auto'
            }}
            dangerouslySetInnerHTML={{ __html: formatMarkdown(assessment.assessment) }}
          />
        </div>
      )}
    </div>
  );
};
